import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles,
  Code2,
  Bot,
  Globe,
  Workflow,
  Clock,
  GitBranch,
  Image,
  Zap,
  Timer,
  BarChart3,
  Shield,
  Layers,
  Send,
  ListTodo,
  Play,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  Paperclip,
  Mic,
  CalendarClock,
  Puzzle,
  Monitor,
  Search,
  FileText,
  Terminal,
  Eye,
  PenTool,
  MessageSquare,
  Save,
  Star,
  Plus,
  LayoutDashboard,
  FolderTree,
  Sliders,
  Users,
  Lock,
  Settings,
  Bookmark,
  CheckCircle2,
  Camera,
  MousePointer2,
  Keyboard,
  SquareStack,
} from 'lucide-react';
import styles from './OnboardingTutorial.module.css';

// ── Custom icons matching the actual app ──────────────────────

function BrowserIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="18" rx="2" /><line x1="2" y1="9" x2="22" y2="9" />
      <circle cx="6" cy="6" r="1" fill="currentColor" /><circle cx="10" cy="6" r="1" fill="currentColor" />
    </svg>
  );
}

// ── Section config ──────────────────────────────────────────────

type TutorialType = 'session' | 'dashboard' | 'taskingbot' | 'workflows' | 'timekeeper' | 'repoforge';

interface TutorialStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  tip?: string;
}

interface SectionSplash {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  features: { icon: React.ReactNode; label: string }[];
  steps: TutorialStep[];
}

const SECTION_SPLASH: Record<TutorialType, SectionSplash> = {
  dashboard: {
    title: 'Your Command Center',
    subtitle: 'Manage all your sessions, launch new projects, and track everything from one place.',
    icon: <LayoutDashboard size={40} />,
    color: '#f59e0b',
    features: [
      { icon: <Plus size={18} />, label: 'Create Sessions' },
      { icon: <Star size={18} />, label: 'Favorites & Filters' },
      { icon: <Workflow size={18} />, label: 'Workflows' },
    ],
    steps: [
      {
        title: 'Create a New Session',
        description: 'Click the "+ Create Session" button in the top right. Describe what you want to build — "a landing page for my startup" or "a React dashboard with charts." AI generates the entire project.',
        icon: <Plus size={22} />,
        tip: 'Be specific: "a restaurant website with menu, reservations, and contact form" works better than "a website."',
      },
      {
        title: 'Filter & Organize',
        description: 'Use the filter tabs to view All, Active, Draft, Favorites, or Archived sessions. Star important sessions to find them fast. The search bar filters by name in real-time.',
        icon: <Star size={22} />,
        tip: 'Star your active projects so they show up first in the Favorites tab.',
      },
      {
        title: 'Session Stats',
        description: 'The stat cards at the top show your total sessions, active projects, drafts, favorites, and archived count. Click any card to jump to that filtered view.',
        icon: <BarChart3 size={22} />,
      },
      {
        title: 'Quick Actions',
        description: 'Each session card has quick actions: open, rename, duplicate, archive, or delete. Right-click or use the menu icon for more options.',
        icon: <Settings size={22} />,
      },
    ],
  },

  session: {
    title: 'Your AI Workspace',
    subtitle: 'A full development environment — code editor, live preview, terminal, and AI assistant in one screen.',
    icon: <Code2 size={40} />,
    color: '#3b82f6',
    features: [
      { icon: <MessageSquare size={18} />, label: 'AI Chat' },
      { icon: <Code2 size={18} />, label: 'Code Editor' },
      { icon: <Eye size={18} />, label: 'Live Preview' },
      { icon: <Terminal size={18} />, label: 'Terminal' },
    ],
    steps: [
      {
        title: 'AI Chat Panel',
        description: 'The left panel is your AI assistant. Tell it what to build: "add a dark mode toggle in the header" or "fix the login form validation." It reads your code, makes changes, and explains what it did.',
        icon: <MessageSquare size={22} />,
        tip: 'Use /plan to create a step-by-step plan before building. Use /review to get a code review.',
      },
      {
        title: 'File Explorer',
        description: 'The file tree shows your entire project. Click any file to open it in the editor. Right-click for options: rename, delete, duplicate, or create new files and folders.',
        icon: <FolderTree size={22} />,
      },
      {
        title: 'Code Editor',
        description: 'Full-featured Monaco editor with syntax highlighting, autocomplete, and error detection. Edit files directly — changes sync instantly with the live preview.',
        icon: <Code2 size={22} />,
        tip: 'The editor supports multiple tabs. Click the split icon to view files side-by-side.',
      },
      {
        title: 'Live Preview',
        description: 'The right panel renders your app in real-time. Switch between desktop, tablet, and mobile views. Use the inspect tool (pencil icon) to click any element and edit it with AI.',
        icon: <Eye size={22} />,
        tip: 'Click the pencil icon in the preview toolbar, then click any element to select it. Choose "Regen" to have AI redesign it.',
      },
      {
        title: 'Terminal',
        description: 'A real terminal tab at the bottom. Run npm commands, install packages, start dev servers, or run scripts. It\'s a full shell — everything works.',
        icon: <Terminal size={22} />,
      },
      {
        title: 'Checkpoints & Version Control',
        description: 'Every change is tracked. Use /checkpoint to save a named snapshot. Use /rollback to restore any previous state. Never lose work — it\'s like unlimited undo.',
        icon: <Save size={22} />,
        tip: 'Create a checkpoint before major changes so you can always go back.',
      },
      {
        title: 'Slash Commands',
        description: '50+ commands available. Type / in the chat to see them all. Key ones: /plan (plan work), /build (execute), /review (code review), /debug (fix issues), /test (generate tests), /deploy (publish live).',
        icon: <Sliders size={22} />,
      },
      {
        title: 'Publish & Deploy',
        description: 'Click "Publish" to deploy your app live to a public URL. It deploys to Cloudflare Pages — global CDN, HTTPS, zero config. Share the link with anyone.',
        icon: <Globe size={22} />,
      },
    ],
  },

  taskingbot: {
    title: 'TaskingBot',
    subtitle: 'Your AI that browses the web, generates images and documents, manages tasks, and works autonomously.',
    icon: <Bot size={40} />,
    color: '#8b5cf6',
    features: [
      { icon: <BrowserIcon size={18} />, label: 'Cloud Browser' },
      { icon: <Image size={18} />, label: 'Media Generation' },
      { icon: <ListTodo size={18} />, label: 'Task Board' },
      { icon: <CalendarClock size={18} />, label: 'Scheduling' },
    ],
    steps: [
      {
        title: 'Chat Interface',
        description: 'Type anything — questions, requests, or instructions. TaskingBot understands context and picks the right tools automatically. Attach files with the paperclip, use voice input with the mic.',
        icon: <Send size={22} />,
      },
      {
        title: 'Agent Modes',
        description: 'Four modes in the toolbar: Auto (AI picks), Cloud Browser (headless browsing), Extension (controls your Chrome), Desktop (mouse/keyboard automation). Pick the mode that fits your task.',
        icon: <Bot size={22} />,
        tip: 'Use Cloud Browser for web research and scraping. Use Extension when you need your logged-in sessions (Gmail, Slack, etc.).',
      },
      {
        title: 'Cloud Browser Agent',
        description: 'TaskingBot opens a real browser in the cloud. Watch it navigate, click, type, and screenshot pages in real-time. It fills forms, extracts data, and handles multi-step workflows across sites.',
        icon: <BrowserIcon size={22} />,
        tip: 'The browser viewer shows live screenshots. Click "Take Control" to interact manually (solve CAPTCHAs, enter passwords), then "Resume Bot" to let it continue.',
      },
      {
        title: 'Extension Agent',
        description: 'Connect the TaskingTech browser extension. TaskingBot can then control your actual Chrome browser — it uses your logged-in sessions, cookies, and saved passwords. Perfect for tasks on sites that require auth.',
        icon: <Puzzle size={22} />,
      },
      {
        title: 'Desktop Agent',
        description: 'Connect the desktop agent for full computer control — mouse clicks, keyboard input, screenshots of your entire screen. TaskingBot can automate any desktop app, not just browsers.',
        icon: <Monitor size={22} />,
      },
      {
        title: 'Image Generation',
        description: 'Say "generate a logo for..." or "create an image of..." and TaskingBot produces it inline using FLUX. Images appear directly in chat — click to expand, download, enhance (2x upscale), or remove background.',
        icon: <Image size={22} />,
        tip: 'You can regenerate any image by clicking the refresh icon. Use "enhance" for 2x AI upscaling.',
      },
      {
        title: 'Documents & PDFs',
        description: 'Ask for reports, analyses, or documents. TaskingBot generates formatted PDFs with charts, tables, and styling. Download as PDF or DOCX. Ask for research reports, business plans, or technical docs.',
        icon: <FileText size={22} />,
      },
      {
        title: 'Web Search & Research',
        description: 'TaskingBot searches the web, reads pages, extracts data, and summarizes findings. Ask "research the top 5 competitors" or "find the pricing for X" — it does the legwork.',
        icon: <Search size={22} />,
      },
      {
        title: 'Task Board',
        description: 'Click the board icon to switch to task view. All your tasks organized by status: To Do, In Progress, Done. Each task card shows title, description, priority, due date, and assignee.',
        icon: <ListTodo size={22} />,
        tip: 'Say "create a task to..." in chat. TaskingBot creates it with smart defaults. Tasks can run autonomously — TaskingBot executes them in the background.',
      },
      {
        title: 'Schedule Tasks',
        description: 'Click the clock icon next to the chat input. Pick a frequency: One-time, Daily, Weekdays, Weekly, 2x/week, Monthly, or Custom. Set a time and date. Tasks run automatically on schedule.',
        icon: <CalendarClock size={22} />,
        tip: 'Use "Daily" for recurring reports or monitoring. Use "Weekly" for summary generation. Use "Custom" for complex schedules like "every MWF at 3pm."',
      },
      {
        title: 'Multi-Agent Mode',
        description: 'Toggle between single agent (1x), parallel agents (fork icon — Researcher + Executor + Synthesizer work simultaneously), or sequential agents (loop icon — each builds on the previous result).',
        icon: <Users size={22} />,
      },
      {
        title: 'Model Selection',
        description: 'Choose your AI model from the dropdown. Switch between cloud models (GPT-4, Claude, Qwen) or local Ollama models for privacy. Different models excel at different tasks.',
        icon: <Sparkles size={22} />,
      },
    ],
  },

  workflows: {
    title: 'Workflow Automation',
    subtitle: 'Automate multi-step processes — connect triggers, actions, and conditions visually or with plain English.',
    icon: <Workflow size={40} />,
    color: '#f59e0b',
    features: [
      { icon: <Zap size={18} />, label: 'Triggers' },
      { icon: <Layers size={18} />, label: 'Visual Builder' },
      { icon: <Play size={18} />, label: 'Run & Monitor' },
    ],
    steps: [
      {
        title: 'Create a Workflow',
        description: 'Click "New Workflow" and describe what you want in plain English: "when a new task is created, search the web for context, then write a summary and email it to me." AI generates the full workflow.',
        icon: <Plus size={22} />,
      },
      {
        title: 'Triggers',
        description: 'Every workflow starts with a trigger — what kicks it off. Options: Manual (run by hand), Schedule (cron/time-based), Webhook (external API call), or Event (task created, message received, etc.).',
        icon: <Zap size={22} />,
        tip: 'Schedule triggers use cron syntax. "0 9 * * 1-5" = every weekday at 9am. AI converts natural language to cron for you.',
      },
      {
        title: 'Visual Node Editor',
        description: 'Drag and drop nodes to build your workflow. Connect them with wires. Each node is an action: send email, call API, generate document, run TaskingBot, execute code, branch on condition.',
        icon: <Layers size={22} />,
      },
      {
        title: 'Conditions & Branching',
        description: 'Add IF/ELSE nodes to branch based on data. "If the task priority is high, send Slack notification. Otherwise, just log it." Conditions can check any data from previous nodes.',
        icon: <GitBranch size={22} />,
      },
      {
        title: 'Run & Monitor',
        description: 'Hit "Run" to execute your workflow. Watch each node light up as it completes. Green = success, red = error. Click any node to see its input/output data and execution logs.',
        icon: <Play size={22} />,
        tip: 'Failed nodes show the exact error. Fix and re-run — it picks up from where it left off.',
      },
      {
        title: 'Execution History',
        description: 'Every workflow run is logged. View past executions with timestamps, status, duration, and full data flow. Debug issues by replaying the exact sequence of events.',
        icon: <BarChart3 size={22} />,
      },
    ],
  },

  timekeeper: {
    title: 'Timekeeper',
    subtitle: 'Track every minute you spend on projects and tasks. Visual analytics show where your time goes.',
    icon: <Clock size={40} />,
    color: '#10b981',
    features: [
      { icon: <Timer size={18} />, label: 'Start/Stop Timer' },
      { icon: <BarChart3 size={18} />, label: 'Analytics' },
      { icon: <Bookmark size={18} />, label: 'Categories' },
    ],
    steps: [
      {
        title: 'Start a Timer',
        description: 'Click the play button to start tracking. Add a description (what you\'re working on), select a project, and optionally assign a category like "Development", "Design", or "Meeting."',
        icon: <Play size={22} />,
        tip: 'The timer runs in the background — switch between pages, it keeps tracking.',
      },
      {
        title: 'Manual Time Entry',
        description: 'Forgot to start a timer? Add entries manually. Pick the start/end time, add a description, and assign it to a project. Edit any past entry to correct mistakes.',
        icon: <PenTool size={22} />,
      },
      {
        title: 'Projects & Categories',
        description: 'Organize time by projects (client work, personal, etc.) and categories (coding, design, meetings, admin). Color-code them for quick visual identification.',
        icon: <Bookmark size={22} />,
      },
      {
        title: 'Analytics Dashboard',
        description: 'Visual charts show time breakdowns by project, category, day, and week. Bar charts, pie charts, and trend lines. See exactly where your time goes and spot patterns.',
        icon: <BarChart3 size={22} />,
        tip: 'Filter analytics by date range. Compare this week vs last week to track productivity trends.',
      },
      {
        title: 'Time Entries List',
        description: 'Scroll through all your time entries in chronological order. Each shows duration, description, project, and category. Click to edit, bulk-select to delete or move.',
        icon: <ListTodo size={22} />,
      },
    ],
  },

  repoforge: {
    title: 'RepoForge',
    subtitle: 'Full repository management — permissions, branch protection, access control, and team collaboration.',
    icon: <GitBranch size={40} />,
    color: '#ef4444',
    features: [
      { icon: <Shield size={18} />, label: 'Permissions' },
      { icon: <Lock size={18} />, label: 'Branch Protection' },
      { icon: <Users size={18} />, label: 'Team Access' },
    ],
    steps: [
      {
        title: 'Connect Repositories',
        description: 'Link your GitHub repositories. RepoForge syncs branches, pull requests, issues, and collaborators automatically. See all your repos in one unified view.',
        icon: <GitBranch size={22} />,
      },
      {
        title: 'Team Roles & Permissions',
        description: 'Assign roles per repository: Admin (full control), Write (push + merge), Read (view only). Control exactly who can push code, create branches, approve PRs, or manage settings.',
        icon: <Users size={22} />,
        tip: 'Use "Read" access for stakeholders who need visibility but shouldn\'t modify code.',
      },
      {
        title: 'Branch Protection Rules',
        description: 'Protect critical branches. Require pull request reviews before merging. Require CI/CD status checks to pass. Block force pushes. Require signed commits. Set minimum reviewer count.',
        icon: <Lock size={22} />,
      },
      {
        title: 'Pull Request Management',
        description: 'View all open PRs across repos. See review status, CI check results, merge conflicts, and reviewer assignments. Approve, request changes, or merge directly from the dashboard.',
        icon: <CheckCircle2 size={22} />,
      },
      {
        title: 'Access Audit',
        description: 'Track who accessed what and when. See permission changes, branch protection modifications, and team membership updates in an audit log. Stay compliant and secure.',
        icon: <Shield size={22} />,
      },
    ],
  },
};

// ── localStorage helpers ────────────────────────────────────────

const ONBOARDING_SEEN_PREFIX = 'tt_onboarding_v2_';

function getOnboardingKey(type: TutorialType, userId?: string | null): string {
  return userId
    ? `${ONBOARDING_SEEN_PREFIX}${type}_${userId}`
    : `${ONBOARDING_SEEN_PREFIX}${type}`;
}

function markOnboardingSeen(type: TutorialType, userId?: string | null): void {
  try {
    localStorage.setItem(`${ONBOARDING_SEEN_PREFIX}${type}`, 'true');
    if (userId) {
      localStorage.setItem(`${ONBOARDING_SEEN_PREFIX}${type}_${userId}`, 'true');
    }
  } catch { /* ignore */ }
}

function isOnboardingSeen(type: TutorialType, userId?: string | null): boolean {
  try {
    if (localStorage.getItem(`${ONBOARDING_SEEN_PREFIX}${type}`) === 'true') return true;
    if (userId && localStorage.getItem(`${ONBOARDING_SEEN_PREFIX}${type}_${userId}`) === 'true') return true;
  } catch { /* ignore */ }
  return false;
}

// ── Tutorial Component ────────────────────────────────────────────

interface OnboardingTutorialProps {
  sessionId?: string;
  userId?: string | null;
  type?: TutorialType;
  isLoading?: boolean;
  onComplete: () => void;
}

export const OnboardingTutorial: React.FC<OnboardingTutorialProps> = ({
  sessionId,
  userId,
  type = 'session',
  onComplete,
}) => {
  const [phase, setPhase] = useState<'enter' | 'show' | 'exit'>('enter');
  const [step, setStep] = useState(0); // 0 = splash, 1+ = tutorial steps
  const splash = SECTION_SPLASH[type];
  const totalSteps = splash.steps.length;

  const handleComplete = useCallback(() => {
    if (phase === 'exit') return;
    setPhase('exit');
    markOnboardingSeen(type, userId);
    setTimeout(onComplete, 400);
  }, [phase, type, userId, onComplete]);

  const handleNext = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  }, [step, totalSteps, handleComplete]);

  const handlePrev = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (step > 0) setStep(step - 1);
  }, [step]);

  // Enter -> show
  useEffect(() => {
    const t = setTimeout(() => setPhase('show'), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`${styles.splashOverlay} ${phase === 'exit' ? styles.splashExit : ''}`}
      onClick={handleComplete}
    >
      <div
        className={`${styles.splashScene} ${phase !== 'enter' ? styles.splashSceneIn : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {step === 0 ? (
          <>
            {/* Floating accent ring */}
            <div className={styles.splashRing} style={{ borderColor: splash.color }} />

            {/* Icon */}
            <div className={styles.splashIcon} style={{ background: `linear-gradient(135deg, ${splash.color}, ${splash.color}dd)` }}>
              {splash.icon}
            </div>

            {/* Title */}
            <h1 className={styles.splashTitle}>{splash.title}</h1>
            <p className={styles.splashSubtitle}>{splash.subtitle}</p>

            {/* Feature pills */}
            <div className={styles.splashFeatures}>
              {splash.features.map((f, i) => (
                <div
                  key={i}
                  className={styles.splashFeaturePill}
                  style={{ animationDelay: `${0.4 + i * 0.15}s` }}
                >
                  {f.icon}
                  <span>{f.label}</span>
                </div>
              ))}
            </div>

            {/* Step count */}
            <div style={{ fontSize: '0.7rem', opacity: 0.5, marginTop: 8, color: 'var(--foreground)' }}>
              {totalSteps} steps · {Math.ceil(totalSteps * 0.4)} min read
            </div>

            {/* Start tutorial button */}
            <button
              className={styles.splashStartBtn}
              style={{ background: splash.color }}
              onClick={handleNext}
            >
              Walk me through it <ArrowRight size={14} />
            </button>

            <button className={styles.splashSkipBtn} onClick={handleComplete}>
              I know my way around
            </button>
          </>
        ) : (
          <>
            {/* Progress bar */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: 'rgba(255,255,255,0.08)',
              borderRadius: '12px 12px 0 0',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${(step / totalSteps) * 100}%`,
                background: splash.color,
                transition: 'width 0.3s ease',
              }} />
            </div>

            {/* Step dots */}
            <div className={styles.stepIndicator}>
              {splash.steps.map((_, i) => (
                <div
                  key={i}
                  className={styles.stepDot}
                  style={{
                    background: i + 1 <= step ? splash.color : 'rgba(255,255,255,0.15)',
                    width: i + 1 === step ? 24 : 8,
                    cursor: 'pointer',
                  }}
                  onClick={(e) => { e.stopPropagation(); setStep(i + 1); }}
                />
              ))}
            </div>

            <div className={styles.stepContent}>
              <div
                className={styles.stepIcon}
                style={{ background: `linear-gradient(135deg, ${splash.color}, ${splash.color}dd)` }}
              >
                {splash.steps[step - 1].icon}
              </div>

              <div className={styles.stepNumber}>Step {step} of {totalSteps}</div>
              <h2 className={styles.stepTitle}>{splash.steps[step - 1].title}</h2>
              <p className={styles.stepDescription}>{splash.steps[step - 1].description}</p>

              {/* Pro tip */}
              {splash.steps[step - 1].tip && (
                <div style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  borderRadius: 8,
                  background: `color-mix(in srgb, ${splash.color} 10%, transparent)`,
                  border: `1px solid color-mix(in srgb, ${splash.color} 25%, transparent)`,
                  fontSize: '0.72rem',
                  color: 'color-mix(in srgb, var(--foreground) 80%, transparent)',
                  lineHeight: 1.5,
                }}>
                  <span style={{ fontWeight: 700, color: splash.color }}>Tip: </span>
                  {splash.steps[step - 1].tip}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className={styles.stepNav}>
              <button
                className={styles.stepNavBtn}
                onClick={handlePrev}
                style={{ opacity: step > 1 ? 1 : 0.3 }}
                disabled={step <= 1}
              >
                <ChevronLeft size={16} /> Back
              </button>

              <button
                className={styles.stepNavBtnPrimary}
                style={{ background: splash.color }}
                onClick={handleNext}
              >
                {step === totalSteps ? 'Get Started' : 'Next'} <ChevronRight size={16} />
              </button>
            </div>

            <button className={styles.splashSkipBtn} onClick={handleComplete}>
              Skip
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ── Hooks ───────────────────────────────────────────────────────

export function useShowOnboarding(sessionId: string, userId?: string | null): boolean {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setShouldShow(!isOnboardingSeen('session', userId));
  }, [sessionId, userId]);

  return shouldShow;
}

export function useShowDashboardOnboarding(userId?: string | null): boolean {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setShouldShow(!isOnboardingSeen('dashboard', userId));
  }, [userId]);

  return shouldShow;
}

export function useShowSectionOnboarding(section: TutorialType, userId?: string | null): boolean {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (section === 'session' || section === 'dashboard') return;
    setShouldShow(!isOnboardingSeen(section, userId));
  }, [section, userId]);

  return shouldShow;
}
